import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Client, clientsAPI } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  PlusIcon,
  BuildingIcon,
  EditIcon,
  TrashIcon,
  MailIcon,
  PhoneIcon,
  DollarSignIcon,
  XIcon,
} from '../icons';

// Premium line icons (1.5px stroke weight)
const Icons = {
  plus: <PlusIcon className="" width={18} height={18} strokeWidth={1.5} />,
  building: <BuildingIcon className="" width={20} height={20} strokeWidth={1.5} />,
  edit: <EditIcon className="" width={16} height={16} strokeWidth={1.5} />,
  trash: <TrashIcon className="" width={16} height={16} strokeWidth={1.5} />,
  mail: <MailIcon className="" width={16} height={16} strokeWidth={1.5} />,
  phone: <PhoneIcon className="" width={16} height={16} strokeWidth={1.5} />,
  dollarSign: <DollarSignIcon className="" width={16} height={16} strokeWidth={1.5} />,
  x: <XIcon className="" width={18} height={18} strokeWidth={1.5} />,
};

const ClientManagement: React.FC = () => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { isDark } = useTheme();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    default_rate: '',
  });

  // Horizontal drag scroll
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tableContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !tableContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - tableContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      tableContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-2'
    : 'block text-sm font-medium text-slate-700 mb-2';

  const tableHeaderClass = isDark
    ? 'bg-slate-900/80 text-slate-400'
    : 'bg-slate-50 text-slate-500';

  const tableRowClass = isDark
    ? 'border-slate-700/50 hover:bg-slate-700/30'
    : 'border-slate-100 hover:bg-slate-50';

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientsAPI.getAll();
      setClients(response.data.clients || response.data || []);
    } catch (error: any) {
      setError(error.response?.data?.error || t('errors.fetchClients'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const clientData = {
        ...formData,
        default_rate: formData.default_rate
          ? parseFloat(formData.default_rate)
          : undefined,
      };

      if (editingClient) {
        await clientsAPI.update(editingClient.id, clientData);
      } else {
        await clientsAPI.create(clientData);
      }

      setShowForm(false);
      setEditingClient(null);
      resetForm();
      fetchClients();
    } catch (error: any) {
      setError(error.response?.data?.error || t('errors.saveClient'));
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      company: client.company || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      default_rate: client.default_rate?.toString() || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: t('clients.deleteTitle'),
      message: t('clients.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await clientsAPI.delete(id);
      fetchClients();
    } catch (error: any) {
      setError(error.response?.data?.error || t('errors.deleteClient'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      default_rate: '',
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingClient(null);
    resetForm();
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="animate-pulse">
          <div
            className={`h-10 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-xl w-1/4 mb-6`}
          ></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`h-16 ${isDark ? 'bg-slate-900' : 'bg-slate-200'} rounded-xl`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div
              className={`hidden sm:flex p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
            >
              <span className={isDark ? 'text-violet-400' : 'text-violet-600'}>
                {Icons.building}
              </span>
            </div>
            <h1
              className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('clients.title')}
            </h1>
          </div>
          <p
            className={`${isDark ? 'text-slate-400' : 'text-slate-600'} sm:ml-14`}
          >
            {t('clients.subtitle')}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
          >
            {Icons.plus}
            {t('clients.newClient')}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl">
          {error}
        </div>
      )}

      {/* Client Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div
            className={`${cardClass} rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto`}
          >
            <div className="flex items-center justify-between mb-6">
              <h2
                className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {editingClient
                  ? t('clients.editClient')
                  : t('clients.newClient')}
              </h2>
              <button
                onClick={handleCancel}
                className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
              >
                {Icons.x}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>
                    {t('clients.clientName')} *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className={inputClass}
                    required
                  />
                </div>

                <div>
                  <label className={labelClass}>{t('clients.company')}</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    {t('clients.contactEmail')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    {t('clients.contactPhone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>{t('clients.address')}</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                    rows={2}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    {t('clients.defaultRate')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_rate}
                    onChange={(e) =>
                      setFormData({ ...formData, default_rate: e.target.value })
                    }
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700/50 flex-col-reverse md:flex-row ">
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`px-5 py-2.5 font-medium rounded-xl transition-colors ${
                    isDark
                      ? 'bg-slate-700 hover:bg-slate-600 text-white'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                  }`}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
                >
                  {editingClient
                    ? t('clients.updateClient')
                    : t('clients.createClient')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Clients Table */}
      <div className={`${cardClass} rounded-2xl overflow-hidden`}>
        <div
          className={`px-6 py-4 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
        >
          <h3
            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {t('clients.clientsCount', { count: clients.length })}
          </h3>
        </div>

        {clients.length === 0 ? (
          <div
            className={`p-12 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            <div
              className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
            >
              <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
                {Icons.building}
              </span>
            </div>
            <p className="font-medium">{t('clients.noClients')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-slate-700/50">
              {clients.map((client) => (
                <div key={client.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {client.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <div
                          className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {client.name}
                        </div>
                        {client.company && (
                          <div
                            className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                          >
                            {client.company}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(client)}
                        className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                        title={t('common.edit')}
                      >
                        {Icons.edit}
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className={`p-2 rounded-lg transition-all ${
                          isDark
                            ? 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                            : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                        }`}
                        title={t('common.delete')}
                      >
                        {Icons.trash}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {client.email && (
                      <div
                        className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                      >
                        <span
                          className={
                            isDark ? 'text-slate-500' : 'text-slate-400'
                          }
                        >
                          {Icons.mail}
                        </span>
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    {client.phone && (
                      <div
                        className={`flex items-center gap-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        <span
                          className={
                            isDark ? 'text-slate-500' : 'text-slate-400'
                          }
                        >
                          {Icons.phone}
                        </span>
                        {client.phone}
                      </div>
                    )}
                    {client.default_rate && (
                      <div
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-medium ${
                          isDark
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        }`}
                      >
                        <span
                          className={
                            isDark ? 'text-emerald-500' : 'text-emerald-600'
                          }
                        >
                          {Icons.dollarSign}
                        </span>
                        {client.default_rate}/hr
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div
              ref={tableContainerRef}
              className={`hidden md:block overflow-auto max-h-[calc(100vh-450px)] cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <table className="min-w-full relative">
                <thead className={`sticky top-0 z-10 ${tableHeaderClass}`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('clients.client')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('clients.contact')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('clients.defaultRate')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('clients.projects')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={
                    isDark
                      ? 'divide-y divide-slate-700/50'
                      : 'divide-y divide-slate-100'
                  }
                >
                  {clients.map((client) => (
                    <tr key={client.id} className={tableRowClass}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {client.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div
                              className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                            >
                              {client.name}
                            </div>
                            {client.company && (
                              <div
                                className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                              >
                                {client.company}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          {client.email && (
                            <div
                              className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                            >
                              <span
                                className={
                                  isDark ? 'text-slate-500' : 'text-slate-400'
                                }
                              >
                                {Icons.mail}
                              </span>
                              {client.email}
                            </div>
                          )}
                          {client.phone && (
                            <div
                              className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                            >
                              <span
                                className={
                                  isDark ? 'text-slate-500' : 'text-slate-400'
                                }
                              >
                                {Icons.phone}
                              </span>
                              {client.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {client.default_rate ? (
                          <div
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-sm font-medium ${
                              isDark
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            }`}
                          >
                            <span
                              className={
                                isDark ? 'text-emerald-500' : 'text-emerald-600'
                              }
                            >
                              {Icons.dollarSign}
                            </span>
                            {client.default_rate}/hr
                          </div>
                        ) : (
                          <span
                            className={
                              isDark ? 'text-slate-500' : 'text-slate-400'
                            }
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        —
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(client)}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.edit')}
                          >
                            {Icons.edit}
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                            title={t('common.delete')}
                          >
                            {Icons.trash}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;
